import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import { Mail, ArrowRight } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Section className="pt-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-4">Contact</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
              Let's talk infrastructure.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Questions about the platform, vendor ecosystem, or partnership opportunities? Our team will respond within one business day.
            </p>
            <div className="mt-8 space-y-4">
              <a
                href="mailto:info@noderahealth.com"
                className="flex items-center gap-3 text-sm text-slate-700 hover:text-blue-700 transition-colors"
                data-testid="contact-email-link"
              >
                <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </span>
                info@noderahealth.com
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 space-y-5 shadow-sm"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" required>
                  <input
                    required
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={handle("name")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={handle("email")}
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company / Clinic">
                  <input
                    data-testid="contact-company-input"
                    value={form.company}
                    onChange={handle("company")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Subject">
                  <input
                    data-testid="contact-subject-input"
                    value={form.subject}
                    onChange={handle("subject")}
                    className={inputCls}
                  />
                </Field>
              </div>
              <Field label="Message" required>
                <textarea
                  required
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={handle("message")}
                  rows={5}
                  className={`${inputCls} resize-y`}
                />
              </Field>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {submitting ? "Sending…" : (<>Send message <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </div>
        </div>
      </Section>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-blue-600">*</span>}
      </span>
      {children}
    </label>
  );
}
